// @ts-strict-ignore
import * as React from 'react';
import CommonNotificationSettingsModal from '../sharedComponents/CommonNotificationSettingsModal';
import {
    tryUpdateAppSettings,
    tryUpdateAppSettingsV2,
    UpdateNotificationSettingsRequest,
    UpdateStepNotificationConfigRequest,
} from '../../actions/appSettings';
import { useDispatch, useSelector } from 'react-redux';
import { AppSettingsUpdatable, AppSettingsV2Updatable } from '../../actions/updatable';
import { RootState } from '../../reducers/reducers';
import { selectCommonNotificationFromAppNotificationId, selectWorkflowId } from '../../selectors/selectors';
import { AppComponentProps } from '../../typescriptHelpers';
import { AppNotificationType, StepNotificationConfigOverride } from '../../types';
import { dayjs } from '@/extendedDayjs';

interface NotificationSettingsModalProps extends AppComponentProps {
    notificationId: AppNotificationType;
    onClose: () => void;
    onAfterClose: () => void;
    open: boolean;

    // Step override props — when provided, the modal creates/updates a step override instead of app-level settings
    stepConfigId?: string;
    stepConfigName?: string;
    workflowId?: string;
    override?: StepNotificationConfigOverride | null;
}

const NotificationSettingsModal: React.FC<NotificationSettingsModalProps> = (props: NotificationSettingsModalProps) => {
    const dispatch = useDispatch();
    const isStepOverride = props.stepConfigId != null;
    const isCreatingOverride = isStepOverride && props.override == null;

    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, props.appId));

    const onSubmit = (values: any) => {
        const hourOfDay = values.hourOfDay ? dayjs(values.hourOfDay, 'HH:mm').clone().utc().hour() : null;

        if (isStepOverride) {
            const request: UpdateStepNotificationConfigRequest = {
                orgId: props.orgId,
                appId: props.appId,
                workflowId: props.workflowId ?? workflowId,
                stepConfigId: props.stepConfigId,
                notificationId: props.notificationId,
                enabled: values.enabled,
                settings: {
                    ...values,
                    hourOfDayForNotification: hourOfDay,
                },
                channels: values.channels ?? [],
            };
            dispatch(
                tryUpdateAppSettingsV2(
                    AppSettingsV2Updatable.UpdateStepNotificationConfig,
                    request,
                    props.notificationId,
                ),
            );
        } else {
            const request: UpdateNotificationSettingsRequest = {
                orgId: props.orgId,
                appId: props.appId,
                notificationId: props.notificationId,
                hourOfDayForNotification: hourOfDay,
                appleProfileAndCertificateReminderDaysBeforeExpiration:
                    values.appleProfileAndCertificateReminderDaysBeforeExpiration,
                bundleSizeIncreaseConditions: values.bundleSizeIncreaseConditions,
                bundleSizeCellularDownloadLimitThresholdMB: values.bundleSizeCellularDownloadLimitThresholdMB,
                bundleSizeAlertNotifyFor: values.bundleSizeAlertNotifyFor,
                ...values,
            };
            dispatch(
                tryUpdateAppSettings(AppSettingsUpdatable.UpdateNotificationSetting, request, props.notificationId),
            );
        }
    };

    const appNotification = useSelector((state: RootState) =>
        selectCommonNotificationFromAppNotificationId(state, props.appId, props.notificationId),
    );

    // For step overrides with existing settings, merge override settings on top of app-level
    const notification = React.useMemo(() => {
        if (!isStepOverride || !props.override) return appNotification;
        return {
            ...appNotification,
            ...(props.override.settings ?? {}),
            enabled: props.override.enabled,
            ...(props.override.channels?.length > 0 ? { channels: props.override.channels } : {}),
        };
    }, [appNotification, isStepOverride, props.override]);

    const actionType =
        isStepOverride ?
            AppSettingsV2Updatable.UpdateStepNotificationConfig
        :   AppSettingsUpdatable.UpdateNotificationSetting;

    const actionButtonText =
        isCreatingOverride ? `Create override for ${props.stepConfigName}`
        : isStepOverride ? `Update override for ${props.stepConfigName}`
        : undefined;

    return (
        <CommonNotificationSettingsModal
            onSubmit={onSubmit}
            actionType={actionType}
            notification={notification}
            actionButtonText={actionButtonText}
            {...props}
        />
    );
};

export default NotificationSettingsModal;
